/**
 *
 * "The Deck History page""
 *
 * @file   DeckHistoryPage.js
 * @author Lateral
 * @since  2023
 */
import { DeckCanvas, FormDialog, SCOPES } from 'components'
import { Box, Grid, Typography } from '@mui/material'
import BomTable from 'components/tables/bom/BomTable'
import React, { useEffect, useMemo, useState } from 'react'
import { useCurrentDeckContext } from 'components/currentDeckContext/CurrentDeckContext'
import { DeckMode } from 'common/deckMode'
import HistoryTable from 'components/tables/history/HistoryTable'
import { updateRevision } from 'data/revisionOperations'
import dayjs from 'dayjs'
import { updateDeckRevisionHistory } from '../common/historyData'
import { EditHistoryForm } from './components/EditHistoryForm'
import { EditRevisionForm } from './components/EditRevisionForm'
import { useLocation, useNavigate } from 'react-router-dom'
import { useCurrentUser } from 'hooks'
import { Storage } from 'aws-amplify'
import { blobToBase64 } from 'common/converters'
import { dateFormat } from 'common/dates'
import ViewUpdatesModal from './components/ViewUpdatesModal'
import { MobileDeckCanvas } from 'components/mobileDeckCanvas/MobileDeckCanvas'
import { DeckLayoutContainer } from '../common/DeckLayoutContainer'
import { useOnBomClick } from 'hooks/useOnBomClick'
import Legend from '../maintenance/mobile/components/Legend'
import { useIndexedDB } from 'react-indexed-db-hook'
import { FILE_UPLOAD_DB_NAME } from 'context/BackgroundProcessContext'
import { useNetworkState } from 'react-use'
import { BackdropLoader } from 'components/BackdropLoader'
import { useSnackbar } from 'notistack'
import { DeckCanvasTab } from '../common/DeckCanvasTab'

function DeckHistoryPage() {
  /**
   * Generates Deck History Page
   *
   * @function
   *
   * @returns {object} - Page for Deck History
   */
  const [selected, setSelected] = useState([])
  const [showLoader, setShowLoader] = useState(false)
  const [tabValue, setTabValue] = useState('1')

  const { materialSelected, onBomClick, setMaterialSelected } = useOnBomClick(() => {
    setSelected([])
  })
  const [selectedDeckRevision, setSelectedDeckRevision] = useState('')
  const [selectedDeckRevisionHistory, setSelectedDeckRevisionHistory] = useState('')
  const [editRevisionModalState, setEditRevisionModalState] = useState({ isOpen: false })
  const [editHistoryModalState, setEditHistoryModalState] = useState({ isOpen: false })
  const [viewUpdatesModalState, setViewUpdatesModalState] = useState({ isOpen: false })
  const [disableToggles, setDisableToggles] = useState(false)
  const {
    deckId,
    setDeckRevision,
    database,
    setInMaintenanceMode,
    setMaintenanceData,
    deckRevisions,
    histories,
    setCurrentMaintenanceView
  } = useCurrentDeckContext()
  const navigate = useNavigate()
  const { state } = useLocation()
  const { getTransactionMetaData, currentUser } = useCurrentUser()
  const { enqueueSnackbar } = useSnackbar()

  const hasMaintenance = currentUser?.hasAllScopes([SCOPES.deckMaintenance.Read])

  const { online } = useNetworkState()
  const { getAll } = useIndexedDB(FILE_UPLOAD_DB_NAME)

  const deck = useMemo(() => database.decks.find((d) => d.id === deckId), [deckId])

  const deckRevision = useMemo(
    () => structuredClone(database.deckRevisions.find((d) => d.id === selectedDeckRevision)),
    [selectedDeckRevision]
  )
  const deckRevisionHistory = useMemo(
    () => structuredClone(database.deckRevisionHistories.find((d) => d.id === selectedDeckRevisionHistory)),
    [selectedDeckRevisionHistory]
  )

  useEffect(() => {
    if (state?.incomingId) {
      const history = database.deckRevisionHistories.find((d) => d.id === state.incomingId)
      setSelectedDeckRevision(history?.RevisionId)
      setSelectedDeckRevisionHistory(history?.id)
    } else {
      const topRevision = deckRevisions[0]
      const topHistory = histories[0]

      if (topRevision) {
        if (
          dayjs(topRevision?.createdAt).isAfter(dayjs(topHistory?.DatePerformed ?? topHistory?.createdAt)) ||
          !topHistory ||
          !hasMaintenance
        ) {
          setSelectedDeckRevision(topRevision?.id)
          setSelectedDeckRevisionHistory('')
        } else {
          setSelectedDeckRevision(topHistory?.RevisionId)
          setSelectedDeckRevisionHistory(topHistory?.id)
        }
      }
    }
  }, [hasMaintenance, deckId, state?.incomingId, histories, deckRevisions])

  // Action on clisking revision rows
  function onRevisionClick(history) {
    setCurrentMaintenanceView('layout')
    setDisableToggles(history.type !== 'Maintenance')
    setSelectedDeckRevision(history.id)
    setSelectedDeckRevisionHistory(history.historyId)
  }

  // Action on changing panel selection
  function onSelectChange(data) {
    setSelected(data)
    setMaterialSelected('')
  }

  // Action editing deck revision
  function onEditHistory(context) {
    const deckRevision_ = database.deckRevisions.find((d) => d.id === context.id)
    if (deckRevision_) {
      const isEditingOld = context.isOld
      setDeckRevision(structuredClone(deckRevision_))
      navigate('/equipment/edit', { state: { isEditingOld } })
    }
  }

  // Action editing maintenance
  async function onEditMaintenance(context) {
    setShowLoader(true)
    const maintenance = database.deckRevisionHistories.find((d) => d.id === context.historyId)
    const deckRevision = database.deckRevisions.find((d) => d.id === context.id)
    const offlineStoredPhotos = await getAll()

    const getPhotoInOfflineStorage = (key) => {
      for (const photo of offlineStoredPhotos) {
        if (photo.path === key) {
          return photo
        }
      }
      return undefined
    }

    if (maintenance && deckRevision) {
      setDeckRevision(structuredClone(deckRevision))
      setInMaintenanceMode(true)
      const photos = []
      for (const photo of maintenance.Photos) {
        const splitKey = photo.Key.split('.')

        const offlineStoredPhoto = getPhotoInOfflineStorage(photo.Key)
        if (online && !offlineStoredPhoto) {
          try {
            const blob = await Storage.get(photo.Key, { download: true })
            //split into 'name' and 'filetype' e.g. abcdefg.jpg
            if (blob) {
              photos.push({
                file: {
                  name: photo.Key,
                  type: splitKey[1],
                  data: await blobToBase64(blob.Body)
                },
                caption: photo.Caption,
                height: photo.Height,
                width: photo.Width,
                key: photo.Key
              })
            }
          } catch {
            enqueueSnackbar('An error occurred while retrieving photo', 'error')
          }
        } else if (offlineStoredPhoto) {
          photos.push({
            file: {
              name: photo.Key,
              type: splitKey[1],
              data: await blobToBase64(offlineStoredPhoto.blob)
            },
            caption: photo.Caption,
            height: photo.Height,
            width: photo.Width,
            key: photo.Key
          })
        } else {
          enqueueSnackbar(`Photo '${photo.Key}' not found in local storage`, 'error')
          photos.push({
            caption: photo.Caption,
            height: photo.Height,
            width: photo.Width,
            key: photo.Key
          })
        }
      }
      const clone = structuredClone(maintenance)
      if (clone.NextShutDownDate) {
        clone.NextShutDownDate = dayjs(clone.NextShutDownDate)
      }
      setMaintenanceData({
        deckRevisionHistory: clone,
        photos: photos
      })
      navigate('/equipment/maintenance', { state: { isEditingOld: true } })
      setShowLoader(false)
    }
  }

  //Action on edting deck Maintenance or Revision details.
  //The edit menu changes, depending on if the selected row is a Revision or Maintenance
  function onEditDetails(context) {
    if (context.isHistory) {
      setEditHistoryModalState({
        isOpen: true,
        title: 'Edit Maintenance Details',
        buttonText: 'Update',
        data: { Id: context.historyId, Description: context.description, Date: dayjs(context.reported) }
      })
    } else {
      setEditRevisionModalState({
        isOpen: true,
        title: 'Edit Revision Details',
        buttonText: 'Update',
        data: { Id: context.id, Description: context.description }
      })
    }
  }

  async function onEditRevisionConformation(modalState) {
    if (modalState?.isSave) {
      let existing = database.deckRevisions.find((d) => d.id === modalState.data.Id)
      if (existing) {
        let clone = structuredClone(existing)
        clone.Description = modalState.data.Description
        await updateRevision(existing, clone, getTransactionMetaData())
      }
    }

    setEditRevisionModalState({ isOpen: false })
  }

  async function onEditHistoryConformation(modalState) {
    if (modalState?.isSave) {
      let existing = database.deckRevisionHistories.find((d) => d.id === modalState.data.Id)
      if (existing) {
        let clone = structuredClone(existing)
        clone.Description = modalState.data.Description
        clone.ShortDescription = modalState.data.Description
        clone.DatePerformed = modalState.data.Date.toISOString()
        if (!clone.CustomerId) {
          clone.CustomerId = deck.CustomerId
        }

        await updateDeckRevisionHistory(existing, clone, getTransactionMetaData())
      }
    }

    setEditHistoryModalState({ isOpen: false })
  }

  function onViewUpdates(history) {
    setViewUpdatesModalState({
      isOpen: true,
      title: `Updates for Maintenance on ${history.created.format(dateFormat)}`,
      buttonText: '',
      data: history.updates
    })
  }

  function onPanelClick(selected) {
    if (selected) {
      setSelected(Array.isArray(selected) ? selected : [selected])
    } else {
      setSelected([])
    }
    setMaterialSelected('')
  }

  //don't show data if context missing
  if (!deck || !deckRevision) {
    return null
  }

  if (Object.entries(deck).length <= 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Typography variant="h3">No Deck Revision found.</Typography>
      </Box>
    )
  }

  return (
    <>
      <DeckLayoutContainer
        canToggle={tabValue === '1'}
        showLayoutToggle
        disableToggles={disableToggles}
        headerTitle="Deck History"
        Deck={
          <DeckCanvasTab
            setTabValue={setTabValue}
            tablValue={tabValue}
            threeDCanvas={
              <DeckCanvas
                data={deckRevision}
                maintenance={deckRevisionHistory}
                selected={selected}
                materialSelected={materialSelected}
                onSelectChange={onSelectChange}
                mode={selectedDeckRevisionHistory ? DeckMode.maintenance : DeckMode.default}
              />
            }
            twoDCanvas={
              <MobileDeckCanvas
                data={deckRevision}
                maintenance={deckRevisionHistory}
                selected={selected}
                materialSelected={materialSelected}
                onSelectChange={onSelectChange}
                mode={selectedDeckRevisionHistory ? DeckMode.maintenance : DeckMode.default}
                onPanelClick={onPanelClick}
              />
            }
          />
        }
        topRightElement={
          <Box>
            <Grid item xs={12}>
              <Legend />
              <HistoryTable
                deckRevisionId={selectedDeckRevision}
                historyId={selectedDeckRevisionHistory}
                deckRevisions={deckRevisions}
                deckRevisionHistories={histories}
                onClick={onRevisionClick}
                onEditDetails={onEditDetails}
                onEditHistory={onEditHistory}
                onEditMaintenance={onEditMaintenance}
                onViewUpdates={onViewUpdates}
              />
            </Grid>

            <BomTable
              fullHeight
              data={deckRevision}
              selected={selected}
              materialSelected={materialSelected}
              onClick={onBomClick}
            />
          </Box>
        }
        deckRevision={deckRevision}
      />
      {editRevisionModalState.isOpen && (
        <FormDialog modalState={editRevisionModalState} onOpenChange={onEditRevisionConformation}>
          <EditRevisionForm />
        </FormDialog>
      )}
      {editHistoryModalState.isOpen && (
        <FormDialog modalState={editHistoryModalState} onOpenChange={onEditHistoryConformation}>
          <EditHistoryForm />
        </FormDialog>
      )}
      {/* {restoreModalState.isOpen && (
        <FormDialog modalState={restoreModalState} onOpenChange={onRestoreConfirmation} center>
          <RestoreForm />
        </FormDialog>
      )} */}
      {viewUpdatesModalState.isOpen && (
        <FormDialog
          modalState={viewUpdatesModalState}
          onOpenChange={() => setViewUpdatesModalState({ isOpen: false })}
          center
          hideActions>
          <ViewUpdatesModal />
        </FormDialog>
      )}

      <BackdropLoader open={showLoader} />
    </>
  )
}

export default DeckHistoryPage
